import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #contextLoading = false;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    const contextElement = this.querySelector('[data-gc-context]');
    if (contextElement) {
      let context;
      try { context = JSON.parse(contextElement.textContent); } catch {}
      if (context?.primary?.length || context?.fallback?.length) {
        this.#loadContextRecommendations(context, productId, sectionId);
        return;
      }
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }


  async #loadContextRecommendations(context, productId, sectionId) {
    if (this.#contextLoading) return;
    this.#contextLoading = true;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    const limit = Number(this.dataset.gcLimit) || 6;
    const selected = [];
    const seen = new Set([String(productId)]);
    let baseDocument;
    let sourceUrl;
    let sourceTitle;
    const collect = async (paths) => {
      for (const path of [...new Set(paths || [])]) {
        if (!path || selected.length >= limit) break;
        const url = new URL(path, location.origin);
        if (url.origin !== location.origin || !url.pathname.includes('/collections/')) continue;
        url.searchParams.set('sort_by', 'best-selling');
        url.searchParams.set('section_id', sectionId);
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) continue;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('product-recommendations');
          if (!root) continue;
          for (const card of root.querySelectorAll('[data-testid="resource-list-grid"] > [data-gc-product-id]')) {
            const id = card.dataset.gcProductId;
            if (!id || seen.has(id) || card.dataset.gcProductKind !== context.kind) continue;
            seen.add(id);
            selected.push(card);
            if (!baseDocument) {
              baseDocument = doc;
              sourceUrl = new URL(path, location.origin);
              sourceUrl.searchParams.set('sort_by', 'best-selling');
              sourceTitle = doc.querySelector('[data-gc-source-title]')?.content?.textContent?.trim();
            }
            if (selected.length >= limit) break;
          }
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }
      }
    };
    try {
      await collect(context.primary);
      if (!selected.length) {
        for (const path of context.fallback || []) {
          await collect([path]);
          if (selected.length) break;
        }
      }
      if (!this.isConnected || controller.signal.aborted) return;
      if (!selected.length || !baseDocument) {
        this.#handleError(new Error('No relevant products available'));
        return;
      }
      const root = baseDocument.querySelector('product-recommendations');
      const grid = root.querySelector('[data-testid="resource-list-grid"]');
      grid.replaceChildren(...selected);
      const moreLink = root.querySelector('[data-gc-collection-link]');
      if (moreLink && sourceUrl) {
        moreLink.href = sourceUrl.href;
        if (sourceTitle) moreLink.textContent = sourceTitle;
        moreLink.hidden = false;
      }
      for (const extra of root.querySelectorAll('.resource-list:not([data-testid="resource-list-grid"])')) extra.remove();
      root.dataset.productId = productId;
      root.dataset.recommendationsPerformed = 'true';
      root.dataset.gcContext = 'ranked-collections';
      root.dataset.gcLimit = String(limit);
      this.dataset.recommendationsPerformed = 'true';
      morphSection(sectionId, baseDocument.documentElement.outerHTML, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (error.name !== 'AbortError') this.#handleError(error);
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
      this.#contextLoading = false;
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
